import React, { useState, useContext } from 'react'
import { Button } from '@deal/components'
import { Link } from 'react-router-dom'
import { Hate, Like, Love, Skip } from '#src/components/SellableReactions/reactions'
import SidebarAwareGrid from '#src/components/SidebarAwareGrid'
import { walkthroughStore } from '#src/stores'
import defaultGrid from './departments/default/grid'
import { SellableCardCarouselProps } from '#src/components/SellableCardCarousel'
import yachtChartersGrid from './departments/yacht-charters/grid'
import { AnalyticsContext } from '#src/containers/Analytics'
import {
  SellableList as DefaultSellableList,
  FeaturedSellable as DefaultFeaturedSellable,
  TitleBlock as DefaultTitleBlock
} from './departments/default'
import {
  SellableList as YachtChartersSellableList,
  FeaturedSellable as YachtChartersFeaturedSellable,
  TitleBlock as YachtChartersTitleBlock
} from './departments/yacht-charters'
import {
  SaveRecommendedListRatingComponent,
  ConsumerSellableReactionRating,
  DepartmentType
} from '#src/generated/graphql'
import styles from './styles.css'
import { getSellableEcommerceProperties } from '#src/services/tracking'

interface SellableCardCarouselWithTitleProps extends SellableCardCarouselProps {
  title: React.ReactNode
  subTitle?: React.ReactNode
  department: DepartmentType
  aspectRatio?: 'tall' | 'wide'
}

const V2: React.SFC<SellableCardCarouselWithTitleProps> = ({
  sellables,
  department,
  title,
  subTitle
}) => {
  if (!sellables || sellables.length < 4) {
    return null
  }

  const RATING_CAROUSEL_FIRST_LOVE_MESSAGE = 'rating_carousel_first_love_message'
  const RATING_CAROUSEL_FIRST_LIKE_MESSAGE = 'rating_carousel_first_like_message'
  const RATING_CAROUSEL_FIRST_DISLIKE_MESSAGE = 'rating_carousel_first_dislike_message'

  const userSeenFirstLoveMessage = walkthroughStore.has(RATING_CAROUSEL_FIRST_LOVE_MESSAGE)
  const userSeenFirstLikeMessage = walkthroughStore.has(RATING_CAROUSEL_FIRST_LIKE_MESSAGE)
  const userSeenFirstDislikeMessage = walkthroughStore.has(RATING_CAROUSEL_FIRST_DISLIKE_MESSAGE)

  const sellableRatingQueue = [...sellables]
  const [featuredSellable, setFeaturedSellable] = useState(sellableRatingQueue.shift())
  const [sellableQueue, updateSellableQueue] = useState(sellableRatingQueue)

  const [isMaskVisible, setMaskVisible] = useState(false)
  const [currentMaskRating, setMaskRating] = useState<ConsumerSellableReactionRating | undefined>(
    undefined
  )

  const analytics = useContext(AnalyticsContext)

  /**
   * Yacht charters is currently the only custom department, but we can always expand on this later
   * and break it out to a more formal branch between departments.
   */
  let FeaturedSellable = DefaultFeaturedSellable
  let TitleBlock = DefaultTitleBlock
  let SellableList = DefaultSellableList
  let grid = defaultGrid

  if (department === DepartmentType.YACHT_CHARTERS) {
    FeaturedSellable = YachtChartersFeaturedSellable
    TitleBlock = YachtChartersTitleBlock
    SellableList = YachtChartersSellableList
    grid = yachtChartersGrid
  }

  const nextSellable = (rating: ConsumerSellableReactionRating) => {
    switch (rating) {
      case ConsumerSellableReactionRating.LIKE:
        walkthroughStore.set(RATING_CAROUSEL_FIRST_LIKE_MESSAGE, true)
        break

      case ConsumerSellableReactionRating.LOVE:
        walkthroughStore.set(RATING_CAROUSEL_FIRST_LOVE_MESSAGE, true)
        break

      case ConsumerSellableReactionRating.DISLIKE:
        walkthroughStore.set(RATING_CAROUSEL_FIRST_DISLIKE_MESSAGE, true)
        break
    }

    const modifiedQueue = [...sellableQueue]
    const nextSellable = modifiedQueue.shift()

    nextSellable && updateSellableQueue([...modifiedQueue, nextSellable])

    setFeaturedSellable(nextSellable)

    setMaskVisible(false)
    setMaskRating(undefined)
  }

  return (
    <div className={styles.container}>
      <SidebarAwareGrid
        areas={grid.defaultGrid}
        areasSm={grid.smallGrid}
        areasMd={grid.mediumGrid}
        areasLg={grid.largeGrid}
      >
        {({ Title, Featured, List }) => {
          return (
            <>
              <Title className={styles.titleGrid}>
                <TitleBlock title={title} subTitle={subTitle} />
              </Title>
              <Featured className={styles.featuredGrid}>
                <div className={styles.featuredSellable}>
                  {isMaskVisible && (
                    <div className={styles.mask}>
                      {currentMaskRating === ConsumerSellableReactionRating.LOVE && (
                        <Love width={60} height={60} />
                      )}
                      {currentMaskRating === ConsumerSellableReactionRating.LIKE && (
                        <Like width={60} height={60} />
                      )}
                      {currentMaskRating === ConsumerSellableReactionRating.DISLIKE && (
                        <Hate width={60} height={60} />
                      )}
                      {currentMaskRating === ConsumerSellableReactionRating.SKIP && (
                        <Skip width={60} height={60} />
                      )}
                      {featuredSellable && (
                        <div className={styles.explainer}>
                          {!userSeenFirstLoveMessage &&
                            currentMaskRating === ConsumerSellableReactionRating.LOVE && (
                              <>
                                <div className={styles.explainerTitle}>
                                  {featuredSellable.title} was added to your favorites.
                                </div>
                                <div className={styles.explainerBody}>
                                  We've let your expert know you're interested in this! You can also
                                  access your favorited items <Link to="/favorites">here</Link>.
                                </div>
                                <div className={styles.explainerButton}>
                                  <Button
                                    variant="contrast-outline"
                                    onClick={() => {
                                      nextSellable(ConsumerSellableReactionRating.LOVE)
                                    }}
                                  >
                                    Got it
                                  </Button>
                                </div>
                              </>
                            )}
                          {!userSeenFirstLikeMessage &&
                            currentMaskRating === ConsumerSellableReactionRating.LIKE && (
                              <>
                                <div className={styles.explainerTitle}>
                                  Got it. A potential fit.
                                </div>
                                <div className={styles.explainerBody}>
                                  We've let your expert know you're interested in this.
                                </div>
                                <div className={styles.explainerButton}>
                                  <Button
                                    variant="contrast-outline"
                                    onClick={() => {
                                      nextSellable(ConsumerSellableReactionRating.LIKE)
                                    }}
                                  >
                                    Next one
                                  </Button>
                                </div>
                              </>
                            )}
                          {!userSeenFirstDislikeMessage &&
                            currentMaskRating === ConsumerSellableReactionRating.DISLIKE && (
                              <>
                                <div className={styles.explainerTitle}>
                                  Got it. This one's not for you.
                                </div>
                                <div className={styles.explainerBody}>
                                  We've let your expert know you're not interested in this.
                                </div>
                                <div className={styles.explainerButton}>
                                  <Button
                                    variant="contrast-outline"
                                    onClick={() => {
                                      nextSellable(ConsumerSellableReactionRating.DISLIKE)
                                    }}
                                  >
                                    Got it
                                  </Button>
                                </div>
                              </>
                            )}
                        </div>
                      )}
                    </div>
                  )}
                  {featuredSellable && (
                    <SaveRecommendedListRatingComponent>
                      {saveRecommendedListRatingComponent => {
                        return (
                          <FeaturedSellable
                            sellable={featuredSellable}
                            onSellableRatingSelected={rating => {
                              setMaskVisible(true)
                              setMaskRating(rating)

                              const autoAdvanceCarousel =
                                (rating === ConsumerSellableReactionRating.LIKE &&
                                  userSeenFirstLikeMessage) ||
                                (rating === ConsumerSellableReactionRating.LOVE &&
                                  userSeenFirstLoveMessage) ||
                                (rating === ConsumerSellableReactionRating.DISLIKE &&
                                  userSeenFirstDislikeMessage) ||
                                rating === ConsumerSellableReactionRating.SKIP

                              setTimeout(() => {
                                /**
                                 * Skipping doesn't actually apply a rating
                                 */
                                if (rating === ConsumerSellableReactionRating.SKIP) {
                                  nextSellable(rating)
                                } else {
                                  saveRecommendedListRatingComponent({
                                    variables: {
                                      input: {
                                        sellableId: featuredSellable.id,
                                        rating
                                      }
                                    }
                                  }).then(() => {
                                    if (autoAdvanceCarousel) {
                                      nextSellable(rating)
                                    }

                                    analytics &&
                                      analytics.events.productRated({
                                        ...getSellableEcommerceProperties(featuredSellable),
                                        placement: 'sellable-card-carousel',
                                        rating
                                      })
                                  })
                                }
                              }, 333)
                            }}
                          />
                        )
                      }}
                    </SaveRecommendedListRatingComponent>
                  )}
                </div>
              </Featured>
              <List>
                <SellableList sellables={sellableQueue} />
              </List>
            </>
          )
        }}
      </SidebarAwareGrid>
    </div>
  )
}

export default V2
